// ---------------------------------------------------------------------------
// IMPORTS
// ---------------------------------------------------------------------------

// ------NODE MODULES---------------------------------------------------------

import { useDispatch } from 'react-redux';

import Grid from '@mui/material/Grid';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';

// ------FILE MODULES---------------------------------------------------------
import { ROW_COUNT } from '../../../config/constants.config';

import { clearCustomerCache } from '../../../helpers/reducers/customers';

import EntityRow from '../Rows/EntityRow.component';

import TableCell from '../../shared/Tables/TableCell.component';
import SingleTableRow from '../../shared/Tables/SingleTableRow.component';
import TableHeader from '../../shared/Tables/TableHeader.component';
import SpinnerLoader from '../../shared/Loader/SpinnerLoader.component';
import Hider from '../../shared/Hider/Hider.component';

import './EntityTable.component.css';

// ---------------------------------------------------------------------------
// PRIVATE
// ---------------------------------------------------------------------------
const DEFAULT_TRANSLATE = (string) => {
  return string;
};
const DEFAULT_IS_LOADING = false;
const DEFAULT_ENTITIES = [];
const DEFAULT_ENTITY_COUNT = 0;
const DEFAULT_ROWS_PER_PAGE = ROW_COUNT[0];
const DEFAULT_PAGE = 0;
const DEFAULT_SET_PAGE = () => {};
const DEFAULT_HIDE_PAGINATION = false;
const DEFAULT_HIDE_ALIAS = false;
const DEFAULT_HIDE_TELEPHONE = false;
const DEFAULT_HIDE_CIF = false;
const DEFAULT_TRANSLATE_PREFIX = 'entity-table';

const aliasText = 'alias';
const telepohneText = 'telephone-number';
const cifText = 'cif';

function getHeaders(hideAlias, hideTelephone, hideCif, translate, translatePrefix) {
  return [
    { text: 'social-denomination' },
    { text: aliasText },
    { text: 'email' },
    { text: 'did' },
    { text: telepohneText },
    { text: cifText },
    { text: 'status' }
  ]
    .filter((header) => {
      if (hideAlias && header.text === aliasText) {
        return false;
      }
      if (hideTelephone && header.text === telepohneText) {
        return false;
      }
      if (hideCif && header.text === cifText) {
        return false;
      }
      return true;
    })
    .map((header) => {
      header.text = translate(`${translatePrefix}-${header.text}`);
      return header;
    });
}

// ---------------------------------------------------------------------------
// EXPORTS
// ---------------------------------------------------------------------------
export default function EntityTable(props) {
  const translate = props.translate || DEFAULT_TRANSLATE;
  const isLoading = props.isLoading || DEFAULT_IS_LOADING;
  const entities = props.entities || DEFAULT_ENTITIES;
  const entityCount = props.entityCount || DEFAULT_ENTITY_COUNT;
  const rowsPerPage = props.rowsPerPage || DEFAULT_ROWS_PER_PAGE;
  const page = props.page || DEFAULT_PAGE;
  const setPage = props.setPage || DEFAULT_SET_PAGE;
  const hidePagination = props.hidePagination || DEFAULT_HIDE_PAGINATION;
  const hideAlias = props.hideAlias || DEFAULT_HIDE_ALIAS;
  const hideTelephone = props.hideTelephone || DEFAULT_HIDE_TELEPHONE;
  const hideCif = props.hideCif || DEFAULT_HIDE_CIF;
  const translatePrefix = props.translatePrefix || DEFAULT_TRANSLATE_PREFIX;

  const dispatch = useDispatch();

  const headers = getHeaders(hideAlias, hideTelephone, hideCif, translate, translatePrefix);

  const paginationColSpan = headers.length;
  const spinnerColSpan = headers.length;

  return (
    <Grid item paddingBottom={4}>
      <TableContainer className='entity-table-container'>
        <Table>
          <TableHeader headers={headers} />
          <TableBody>
            <SpinnerLoader
              isLoading={isLoading}
              text={translate(`${translatePrefix}-loading`)}
              component={SingleTableRow}
              componentProps={{ colSpan: spinnerColSpan, cellProps: { useTypography: false } }}>
              {entities.map((entity) => (
                <EntityRow
                  key={entity.customerId}
                  translate={translate}
                  entity={entity}
                  hideAlias={hideAlias}
                  hideTelephone={hideTelephone}
                  hideCif={hideCif}
                />
              ))}
            </SpinnerLoader>
            <Hider isHidden={hidePagination}>
              <TableRow>
                <TableCell colSpan={paginationColSpan} useTypography={false}>
                  <TablePagination
                    component='div'
                    count={entityCount}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={[]}
                    page={page}
                    onPageChange={(event, newPage) => {
                      setPage(newPage);
                      dispatch(clearCustomerCache());
                    }}
                  />
                </TableCell>
              </TableRow>
            </Hider>
          </TableBody>
        </Table>
      </TableContainer>
    </Grid>
  );
}
